import React, { useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Sparkles, Loader2, Check, X, Target, PenTool, GraduationCap, Mic, ImageIcon } from 'lucide-react';
import VoiceButton from '../ui/VoiceButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENTS = [
  { id: 'sales_writer', label: 'Sales Writer', icon: Target, description: 'Persuasive, deal-context aware, DISC-adapted', color: 'amber' },
  { id: 'email_writer', label: 'Email Writer', icon: PenTool, description: 'General-purpose email drafting', color: 'blue' },
  { id: 'communication_coach', label: 'Communication Coach', icon: GraduationCap, description: 'Tone optimization & coaching', color: 'emerald' },
];

/**
 * AI Agent panel for the email builder.
 * Provides voice/text prompting, agent selection, and content generation.
 */
export default function AIAgentPanel({ selectedBlock, allBlocks, onUpdateBlockContent, onInsertImageBlock, contactId, dealId, accountId }) {
  const { getAuthHeader } = useAuth();
  const [agentId, setAgentId] = useState('email_writer');
  const [prompt, setPrompt] = useState('');
  const [scope, setScope] = useState('block'); // 'block' | 'full'
  const [recommendImage, setRecommendImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recommendedImages, setRecommendedImages] = useState([]);

  const agent = AGENTS.find(a => a.id === agentId) || AGENTS[1];

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt first'); return; }

    setLoading(true);
    setResult(null);
    setRecommendedImages([]);

    try {
      // Get existing content for context
      let existingDraft = '';
      if (scope === 'block' && selectedBlock?.block_type === 'text') {
        existingDraft = selectedBlock.props?.content || '';
      } else if (scope === 'full') {
        existingDraft = allBlocks.filter(b => b.block_type === 'text').map(b => b.props?.content || '').join('\n\n');
      }

      const res = await axios.post(`${API}/ai/write-email`, {
        prompt,
        contact_id: contactId,
        deal_id: dealId,
        account_id: accountId,
        existing_draft: existingDraft,
        agent_type: agentId,
        scope,
      }, { headers: getAuthHeader() });

      setResult(res.data);

      // Image recommendation
      if (recommendImage) {
        try {
          const imgRes = await axios.post(`${API}/ai/recommend-image`, {
            email_content: res.data.body || prompt,
          }, { headers: getAuthHeader() });
          setRecommendedImages(imgRes.data.images || []);
        } catch {
          // Non-critical
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (!result) return;
    if (scope === 'block' && selectedBlock) {
      onUpdateBlockContent(selectedBlock.block_id, result.body || result.suggested_rewrite?.body || '');
    } else {
      // Full email — update first text block
      const firstText = allBlocks.find(b => b.block_type === 'text');
      if (firstText) onUpdateBlockContent(firstText.block_id, result.body || '');
    }
    toast.success('AI content applied');
    setResult(null);
    setPrompt('');
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-3 border-b border-stone-200 bg-amber-50/50">
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <p className="text-xs font-semibold text-stone-700">AI Agent</p>
        </div>

        {/* Agent selector */}
        <div className="space-y-1">
          {AGENTS.map(a => {
            const Icon = a.icon;
            const active = agentId === a.id;
            return (
              <button key={a.id} onClick={() => setAgentId(a.id)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-all text-xs ${active ? `bg-${a.color}-50 border-${a.color}-200 text-${a.color}-700` : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{a.label}</p>
                  <p className="text-[10px] opacity-70">{a.description}</p>
                </div>
                {active && <Check className="w-3 h-3 ml-auto" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Prompt */}
      <div className="p-3 space-y-3">
        {/* Scope */}
        <div>
          <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Apply to</p>
          <div className="flex gap-1">
            {[{ id: 'block', label: 'Selected Block' }, { id: 'full', label: 'Entire Email' }].map(s => (
              <button key={s.id} onClick={() => setScope(s.id)}
                className={`flex-1 px-2 py-1.5 text-[10px] font-medium rounded-lg border transition-colors ${scope === s.id ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prompt textarea + voice */}
        <div>
          <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Prompt</p>
          <div className="relative">
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
              placeholder={`Tell the ${agent.label} what to write...`}
              className="w-full px-3 py-2 pr-10 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-none" />
            <div className="absolute right-2 top-2">
              <VoiceButton onTranscript={(text) => setPrompt(prev => prev ? prev + ' ' + text : text)} className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Image recommendation checkbox */}
        <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
          <input type="checkbox" checked={recommendImage} onChange={e => setRecommendImage(e.target.checked)} className="rounded border-stone-300" />
          <ImageIcon className="w-3 h-3" />
          Recommend image from content library
        </label>

        {/* Generate button */}
        <button onClick={handleGenerate} disabled={loading || !prompt.trim()}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-semibold bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {loading ? 'Generating...' : 'Magic Rewrite'}
        </button>

        {/* Result */}
        {result && (
          <div className="border border-stone-200 rounded-lg overflow-hidden">
            <div className="p-2 bg-stone-50 border-b border-stone-100">
              <p className="text-[10px] font-semibold text-stone-500 uppercase">AI Output</p>
            </div>
            <div className="p-3 max-h-60 overflow-y-auto">
              <div className="prose prose-xs max-w-none text-xs" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.body || result.suggested_rewrite?.body || '') }} />
            </div>

            {/* Coaching notes */}
            {(result.coaching_notes?.length > 0 || result.coaching_tips?.length > 0) && (
              <div className="px-3 pb-2">
                <p className="text-[10px] font-semibold text-stone-400 uppercase mb-1">Tips</p>
                <ul className="space-y-0.5">
                  {(result.coaching_notes || result.coaching_tips || []).map((tip, i) => (
                    <li key={i} className="text-[10px] text-stone-500">- {tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Accept / Discard */}
            <div className="flex gap-2 p-2 border-t border-stone-100">
              <button onClick={handleAccept} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800">
                <Check className="w-3 h-3" /> Accept
              </button>
              <button onClick={() => setResult(null)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-white text-stone-600 border border-stone-200 rounded-lg hover:bg-stone-50">
                <X className="w-3 h-3" /> Discard
              </button>
            </div>
          </div>
        )}

        {/* Recommended images */}
        {recommendedImages.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Suggested Images</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {recommendedImages.map((img, i) => (
                <button key={i} onClick={() => onInsertImageBlock(img.url, img.name)}
                  className="flex-shrink-0 w-20 h-20 rounded-lg border border-stone-200 overflow-hidden hover:border-stone-400 transition-colors"
                  title={img.name}>
                  <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
